/**
 * Client-side fidelity helpers for the fixed-DOCX preview (docx-preview).
 *
 * docx-preview renders selectable HTML — which the field-marking step depends on —
 * but it has two fidelity gaps that make uploaded SELISE templates look wrong:
 *
 *  1. FONTS. The browser has none of the document's typefaces (Ubuntu, Open Sans,
 *     Open Sans Light, Exo, Exo Medium are SELISE's brand fonts; the file embeds
 *     none of them), so every run falls back to Times serif. We self-host them under
 *     /public/fonts and declare @font-face under the exact family names docx-preview
 *     emits (the run's `w:ascii`, e.g. "Ubuntu" / "Open Sans Light"), so text renders
 *     and reflows like Word.
 *
 *  2. FLOATING IMAGES. docx-preview's drawing support is thin: it has no `wrapSquare`
 *     case (the image collapses to `float:left` and collides with text), it parses
 *     `behindDoc` then throws the value away (so behind-text watermarks render on top
 *     or not at all), and it ignores `relativeFrom` (page/margin) on the position
 *     anchors. `applyFloatingImageFixups` re-reads the original anchor geometry from
 *     the .docx and absolutely positions each floating image against its page section,
 *     honoring offsets, extent and z-order.
 *
 * Images carry no selectable text, so relocating their wrappers in the DOM never
 * affects the body text model the tokenizer/highlights rely on.
 *
 * Browser port note: anchor parsing reuses `pizzip` (already a tokenizer dep) + the
 * browser's native `DOMParser` instead of the old app's `fflate`.
 */

import PizZip from 'pizzip';

// ---------------------------------------------------------------------------
// Fonts
// ---------------------------------------------------------------------------

const FONT_BASE = '/fonts';

/**
 * Map every typeface our templates reference to a self-hosted file, under the exact
 * family name docx-preview emits. "Open Sans Light" / "Exo Medium" are distinct
 * families in Word (not weights), so they get their own @font-face blocks. Calibri /
 * Aptos are mapped to the bundled Aptos (Office's metric-compatible default) so older
 * Calibri-based templates still reflow faithfully.
 */
export const WORD_FONT_FACE_CSS = `
/* Ubuntu — primary body font of SELISE templates */
@font-face { font-family:'Ubuntu'; src:url('${FONT_BASE}/ubuntu/ubuntu-400.woff2') format('woff2'); font-weight:400; font-style:normal; font-display:swap; }
@font-face { font-family:'Ubuntu'; src:url('${FONT_BASE}/ubuntu/ubuntu-400-italic.woff2') format('woff2'); font-weight:400; font-style:italic; font-display:swap; }
@font-face { font-family:'Ubuntu'; src:url('${FONT_BASE}/ubuntu/ubuntu-500.woff2') format('woff2'); font-weight:500; font-style:normal; font-display:swap; }
@font-face { font-family:'Ubuntu'; src:url('${FONT_BASE}/ubuntu/ubuntu-700.woff2') format('woff2'); font-weight:700; font-style:normal; font-display:swap; }
@font-face { font-family:'Ubuntu'; src:url('${FONT_BASE}/ubuntu/ubuntu-700-italic.woff2') format('woff2'); font-weight:700; font-style:italic; font-display:swap; }

/* Open Sans — document default font */
@font-face { font-family:'Open Sans'; src:url('${FONT_BASE}/open-sans/open-sans-300.woff2') format('woff2'); font-weight:300; font-style:normal; font-display:swap; }
@font-face { font-family:'Open Sans'; src:url('${FONT_BASE}/open-sans/open-sans-400.woff2') format('woff2'); font-weight:400; font-style:normal; font-display:swap; }
@font-face { font-family:'Open Sans'; src:url('${FONT_BASE}/open-sans/open-sans-400-italic.woff2') format('woff2'); font-weight:400; font-style:italic; font-display:swap; }
@font-face { font-family:'Open Sans'; src:url('${FONT_BASE}/open-sans/open-sans-600.woff2') format('woff2'); font-weight:600; font-style:normal; font-display:swap; }
@font-face { font-family:'Open Sans'; src:url('${FONT_BASE}/open-sans/open-sans-700.woff2') format('woff2'); font-weight:700; font-style:normal; font-display:swap; }

/* Open Sans Light — a distinct family name in Word (the cover-page Title style) */
@font-face { font-family:'Open Sans Light'; src:url('${FONT_BASE}/open-sans/open-sans-300.woff2') format('woff2'); font-weight:300; font-style:normal; font-display:swap; }
@font-face { font-family:'Open Sans Light'; src:url('${FONT_BASE}/open-sans/open-sans-300.woff2') format('woff2'); font-weight:400; font-style:normal; font-display:swap; }
@font-face { font-family:'Open Sans Light'; src:url('${FONT_BASE}/open-sans/open-sans-700.woff2') format('woff2'); font-weight:700; font-style:normal; font-display:swap; }

/* Exo / Exo Medium — heading fonts */
@font-face { font-family:'Exo'; src:url('${FONT_BASE}/exo/exo-400.woff2') format('woff2'); font-weight:400; font-style:normal; font-display:swap; }
@font-face { font-family:'Exo'; src:url('${FONT_BASE}/exo/exo-500.woff2') format('woff2'); font-weight:500; font-style:normal; font-display:swap; }
@font-face { font-family:'Exo'; src:url('${FONT_BASE}/exo/exo-700.woff2') format('woff2'); font-weight:700; font-style:normal; font-display:swap; }
@font-face { font-family:'Exo Medium'; src:url('${FONT_BASE}/exo/exo-500.woff2') format('woff2'); font-weight:400; font-style:normal; font-display:swap; }
@font-face { font-family:'Exo Medium'; src:url('${FONT_BASE}/exo/exo-500.woff2') format('woff2'); font-weight:500; font-style:normal; font-display:swap; }
@font-face { font-family:'Exo Medium'; src:url('${FONT_BASE}/exo/exo-700.woff2') format('woff2'); font-weight:700; font-style:normal; font-display:swap; }

/* Aptos — Office's current default (Calibri's successor); also stands in for Calibri */
@font-face { font-family:'Aptos'; src:url('${FONT_BASE}/aptos/aptos-regular.ttf') format('truetype'); font-weight:400; font-style:normal; font-display:swap; }
@font-face { font-family:'Aptos'; src:url('${FONT_BASE}/aptos/aptos-bold.ttf') format('truetype'); font-weight:700; font-style:normal; font-display:swap; }
@font-face { font-family:'Aptos Display'; src:url('${FONT_BASE}/aptos/aptos-bold.ttf') format('truetype'); font-weight:400 700; font-style:normal; font-display:swap; }
@font-face { font-family:'Calibri'; src:url('${FONT_BASE}/aptos/aptos-regular.ttf') format('truetype'); font-weight:400; font-style:normal; font-display:swap; }
@font-face { font-family:'Calibri'; src:url('${FONT_BASE}/aptos/aptos-bold.ttf') format('truetype'); font-weight:700; font-style:normal; font-display:swap; }
@font-face { font-family:'Calibri Light'; src:url('${FONT_BASE}/aptos/aptos-regular.ttf') format('truetype'); font-weight:300 400; font-style:normal; font-display:swap; }
`;

/** Inject the @font-face block once, globally, so docx-preview's light-DOM render picks it up. */
export function ensureWordFontFaces(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById('docx-word-fontfaces')) return;
  const style = document.createElement('style');
  style.id = 'docx-word-fontfaces';
  style.textContent = WORD_FONT_FACE_CSS;
  document.head.appendChild(style);
}

// ---------------------------------------------------------------------------
// Floating-image anchor geometry
// ---------------------------------------------------------------------------

/** English Metric Units per CSS pixel (914400 EMU/in ÷ 96 px/in). */
const EMU_PER_PX = 9525;
const emuToPx = (emu: number): number => emu / EMU_PER_PX;

/** OOXML wrap modes that matter to layout. */
export type WrapKind = 'inline' | 'none' | 'square' | 'tight' | 'through' | 'topAndBottom';

export interface DrawingAnchor {
  /** false for inline drawings (left untouched), true for floating <wp:anchor>. */
  isAnchor: boolean;
  /** How text relates to the image — decides in-flow vs. absolute placement. */
  wrap: WrapKind;
  /** Whether the image sits behind the text layer (watermark). */
  behindDoc: boolean;
  /** Extent (display size) in EMU. */
  wEmu: number;
  hEmu: number;
  /** Horizontal anchor: what the offset/alignment is measured from + the value. */
  hRel: string;
  hOff: number;
  hAlign: string | null;
  /** Vertical anchor. */
  vRel: string;
  vOff: number;
  vAlign: string | null;
}

function localChildren(el: Element, local: string): Element[] {
  // getElementsByTagNameNS('*', local) matches by localName regardless of prefix.
  return Array.from(el.getElementsByTagNameNS('*', local));
}
function firstLocal(el: Element | null, local: string): Element | null {
  if (!el) return null;
  return localChildren(el, local)[0] ?? null;
}

function parsePosition(
  pos: Element | null,
  fallbackRel: string
): { rel: string; off: number; align: string | null } {
  if (!pos) return { rel: fallbackRel, off: 0, align: null };
  const rel = pos.getAttribute('relativeFrom') || fallbackRel;
  const alignEl = firstLocal(pos, 'align');
  const offEl = firstLocal(pos, 'posOffset');
  const off = offEl ? parseInt(offEl.textContent || '0', 10) || 0 : 0;
  const align = alignEl ? (alignEl.textContent || '').trim() || null : null;
  return { rel, off, align };
}

/**
 * Parse `word/document.xml` from a .docx and return its body drawings in document
 * order. Order is the contract used to match these against the rendered <img>
 * elements (docx-preview renders body drawings in the same order).
 */
export function parseDocxAnchors(data: ArrayBuffer | Uint8Array): DrawingAnchor[] {
  try {
    const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
    const file = new PizZip(bytes).file('word/document.xml');
    if (!file) return [];
    const doc = new DOMParser().parseFromString(file.asText(), 'application/xml');
    if (doc.getElementsByTagName('parsererror').length) return [];

    return Array.from(doc.getElementsByTagNameNS('*', 'drawing')).map((drawing) => {
      const anchor = firstLocal(drawing, 'anchor');
      const isAnchor = !!anchor;
      const scope = anchor ?? firstLocal(drawing, 'inline') ?? drawing;

      const extent = firstLocal(scope, 'extent');
      const wEmu = extent ? parseInt(extent.getAttribute('cx') || '0', 10) || 0 : 0;
      const hEmu = extent ? parseInt(extent.getAttribute('cy') || '0', 10) || 0 : 0;

      const h = parsePosition(firstLocal(scope, 'positionH'), 'column');
      const v = parsePosition(firstLocal(scope, 'positionV'), 'paragraph');

      let wrap: WrapKind = isAnchor ? 'none' : 'inline';
      if (anchor) {
        if (firstLocal(anchor, 'wrapSquare')) wrap = 'square';
        else if (firstLocal(anchor, 'wrapTight')) wrap = 'tight';
        else if (firstLocal(anchor, 'wrapThrough')) wrap = 'through';
        else if (firstLocal(anchor, 'wrapTopAndBottom')) wrap = 'topAndBottom';
        else wrap = 'none';
      }

      return {
        isAnchor,
        wrap,
        behindDoc: anchor?.getAttribute('behindDoc') === '1',
        wEmu,
        hEmu,
        hRel: h.rel,
        hOff: h.off,
        hAlign: h.align,
        vRel: v.rel,
        vOff: v.off,
        vAlign: v.align,
      } satisfies DrawingAnchor;
    });
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------
// Floating-image fix-up (post-render DOM pass)
// ---------------------------------------------------------------------------

const SECTION_SELECTOR = 'section.docxpv';

/** Wrap modes where Word reserves space and flows text below/around the image. */
const RESERVE_SPACE_WRAPS = new Set<WrapKind>(['square', 'tight', 'through', 'topAndBottom']);

interface SectionMetrics {
  padL: number;
  padR: number;
  padT: number;
  contentW: number;
  clientH: number;
}

function sectionMetrics(section: HTMLElement): SectionMetrics {
  const cs = getComputedStyle(section);
  const padL = parseFloat(cs.paddingLeft) || 0;
  const padR = parseFloat(cs.paddingRight) || 0;
  const padT = parseFloat(cs.paddingTop) || 0;
  return {
    padL,
    padR,
    padT,
    contentW: section.clientWidth - padL - padR,
    clientH: section.clientHeight,
  };
}

/**
 * Re-place floating images in a freshly rendered docx-preview tree so they match Word.
 * Matches parsed anchors to the rendered body <img>s by document order, then places
 * space-reserving wraps in flow and wrapNone/behind-text images absolutely against the
 * page section. Two passes: in-flow reservations first (they reflow the page), then
 * absolute placement measured against the reflowed layout. Inline drawings are left
 * alone. Idempotent. Call AFTER fonts/images settle and BEFORE measuring natural size.
 */
export function applyFloatingImageFixups(root: HTMLElement, anchors: DrawingAnchor[]): void {
  if (!anchors.length) return;

  const imgs = Array.from(
    root.querySelectorAll<HTMLImageElement>(`${SECTION_SELECTOR} > article img`)
  );
  const n = Math.min(imgs.length, anchors.length);

  type Job = { a: DrawingAnchor; img: HTMLImageElement; box: HTMLElement; section: HTMLElement };
  const reserve: Job[] = [];
  const absolute: Job[] = [];
  for (let i = 0; i < n; i++) {
    const a = anchors[i];
    if (!a.isAnchor) continue;
    const img = imgs[i];
    const box = img.parentElement as HTMLElement | null; // docx-preview's drawing <div>
    const section = img.closest<HTMLElement>(SECTION_SELECTOR);
    if (!box || !section || box.dataset.floatFixed === '1') continue;
    if (!a.behindDoc && RESERVE_SPACE_WRAPS.has(a.wrap)) reserve.push({ a, img, box, section });
    else absolute.push({ a, img, box, section });
  }

  const fillBox = (img: HTMLImageElement) =>
    Object.assign(img.style, { position: 'static', left: '', top: '', width: '100%', height: '100%' });

  // Pass 1 — in-flow reservations (reflow the page).
  for (const { a, img, box, section } of reserve) {
    const { padL } = sectionMetrics(section);
    const w = emuToPx(a.wEmu);
    const h = emuToPx(a.hEmu);
    const offX = emuToPx(a.hOff);
    const marginLeft = a.hRel === 'page' ? offX - padL : offX;
    Object.assign(box.style, {
      position: 'relative',
      float: 'none',
      display: 'block',
      width: w > 0 ? `${Math.round(w)}px` : box.style.width,
      height: h > 0 ? `${Math.round(h)}px` : box.style.height,
      margin: `${Math.round(emuToPx(a.vOff))}px 0 0 ${Math.round(marginLeft)}px`,
      maxWidth: 'none',
      zIndex: '1',
    });
    fillBox(img);
    box.dataset.floatFixed = '1';
  }

  // Pass 2 — absolute placement, measured against the now-reflowed layout.
  for (const { a, img, box, section } of absolute) {
    const { padL, padT, contentW, clientH } = sectionMetrics(section);
    const secTop = section.getBoundingClientRect().top;
    const preTop = box.getBoundingClientRect().top - secTop; // in-flow top, pre-absolute
    const w = emuToPx(a.wEmu);
    const h = emuToPx(a.hEmu);

    let left: number;
    const offX = emuToPx(a.hOff);
    if (a.hAlign === 'center') left = padL + Math.max(0, (contentW - w) / 2);
    else if (a.hAlign === 'right') left = padL + Math.max(0, contentW - w);
    else if (a.hAlign === 'left' || a.hAlign === 'inside') left = padL;
    else if (a.hRel === 'page') left = offX;
    else left = padL + offX; // margin / column / leftMargin / character / ...

    let top: number;
    const offY = emuToPx(a.vOff);
    if (a.vAlign === 'center') top = padT + Math.max(0, (clientH - padT - h) / 2);
    else if (a.vAlign === 'bottom') top = Math.max(0, clientH - h);
    else if (a.vAlign === 'top') top = padT;
    else if (a.vRel === 'page') top = offY;
    else if (a.vRel === 'margin' || a.vRel === 'topMargin') top = padT + offY;
    else top = preTop + offY; // paragraph / line / text — relative to where it flowed

    Object.assign(box.style, {
      position: 'absolute',
      left: `${Math.round(left)}px`,
      top: `${Math.round(top)}px`,
      width: w > 0 ? `${Math.round(w)}px` : box.style.width,
      height: h > 0 ? `${Math.round(h)}px` : box.style.height,
      margin: '0',
      padding: '0',
      float: 'none',
      display: 'block',
      maxWidth: 'none',
      zIndex: a.behindDoc ? '0' : '2',
    });
    fillBox(img);
    box.dataset.floatFixed = '1';

    // Relocate to a direct section child: prepend behind-text watermarks (they paint
    // below article z-index:1), append the rest (they paint above the text).
    if (a.behindDoc) section.insertBefore(box, section.firstChild);
    else section.appendChild(box);
  }
}
